import React, { useState } from 'react';

const CustomerLoginSignup = ({ companyId, companyName, onLoginSuccess, API_URL }) => {
    const [activeTab, setActiveTab] = useState('login'); // 'login' or 'signup'
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Form inputs
    const [username, setUsername] = useState('');
    const [phone, setPhone] = useState('');
    const [dob, setDob] = useState('');
    const [loginIdentifier, setLoginIdentifier] = useState(''); // Username or Phone

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const res = await fetch(`${API_URL}/api/customer/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    company_id: parseInt(companyId),
                    identifier: loginIdentifier.trim()
                })
            });
            const json = await res.json();
            if (json.success && json.data) {
                onLoginSuccess(json.data);
            } else {
                setError(json.message || 'Login failed. Please verify username or phone number.');
            }
        } catch (err) {
            console.error('Login error:', err);
            setError('Network error. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const res = await fetch(`${API_URL}/api/customer/signup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    company_id: parseInt(companyId),
                    username: username.trim(),
                    phone: phone.trim(),
                    dob: dob || null
                })
            });
            const json = await res.json();
            if (json.success && json.data) {
                onLoginSuccess(json.data);
            } else {
                setError(json.message || 'Signup failed. Please try again.');
            }
        } catch (err) {
            console.error('Signup error:', err);
            setError('Network error. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gradient-to-br from-emerald-950 via-teal-900 to-slate-900 overflow-y-auto">
            <div className="bg-white/10 backdrop-blur-md rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl border border-white/20 animate-fade-in-up my-8">
                
                {/* Brand / Header */}
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 text-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/20">
                        <i className="fas fa-utensils text-2xl"></i>
                    </div>
                    <h2 className="text-2xl font-black text-white tracking-tight">Welcome to {companyName || 'DineFlow'}</h2>
                    <p className="text-emerald-200/70 text-xs mt-1">Please log in or sign up to view the menu and place orders</p>
                </div>

                {/* Tab selector */}
                <div className="flex bg-black/20 rounded-xl p-1 mb-6 border border-white/5">
                    <button
                        onClick={() => { setActiveTab('login'); setError(''); }}
                        className={`flex-1 py-2.5 text-xs font-black rounded-lg transition-all ${activeTab === 'login' ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-slate-900 shadow-md' : 'text-emerald-100 hover:text-white'}`}
                    >
                        LOGIN
                    </button>
                    <button
                        onClick={() => { setActiveTab('signup'); setError(''); }}
                        className={`flex-1 py-2.5 text-xs font-black rounded-lg transition-all ${activeTab === 'signup' ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-slate-900 shadow-md' : 'text-emerald-100 hover:text-white'}`}
                    >
                        SIGN UP
                    </button>
                </div>

                {error && (
                    <div className="bg-red-500/20 border border-red-500/30 text-red-200 p-3.5 rounded-xl text-xs font-medium mb-6 animate-pulse flex items-start gap-2">
                        <i className="fas fa-exclamation-circle mt-0.5"></i>
                        <span>{error}</span>
                    </div>
                )}

                {/* Forms */}
                {activeTab === 'login' ? (
                    <form onSubmit={handleLogin} className="space-y-4">
                        <div>
                            <label className="block text-[10px] font-black text-emerald-300 uppercase tracking-widest mb-1.5">Username or Phone Number</label>
                            <div className="relative">
                                <i className="fas fa-user absolute left-4 top-1/2 -translate-y-1/2 text-emerald-300/50"></i>
                                <input
                                    type="text"
                                    required
                                    value={loginIdentifier}
                                    onChange={(e) => setLoginIdentifier(e.target.value)}
                                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-emerald-100/30 focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 focus:bg-white/10 outline-none transition-all text-sm font-medium"
                                    placeholder="Enter username or phone number"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-gradient-to-r from-emerald-400 to-teal-500 hover:from-emerald-500 hover:to-teal-600 text-slate-950 font-black py-3.5 rounded-xl shadow-lg transition-all active:scale-98 disabled:opacity-50 text-sm tracking-wide mt-2"
                        >
                            {isLoading ? 'VERIFYING...' : 'LOGIN & BROWSE MENU'}
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleSignup} className="space-y-4">
                        <div>
                            <label className="block text-[10px] font-black text-emerald-300 uppercase tracking-widest mb-1.5">Username</label>
                            <div className="relative">
                                <i className="fas fa-user absolute left-4 top-1/2 -translate-y-1/2 text-emerald-300/50"></i>
                                <input
                                    type="text"
                                    required
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-emerald-100/30 focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 focus:bg-white/10 outline-none transition-all text-sm font-medium"
                                    placeholder="Enter username"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-[10px] font-black text-emerald-300 uppercase tracking-widest mb-1.5">Phone Number</label>
                            <div className="relative">
                                <i className="fas fa-phone absolute left-4 top-1/2 -translate-y-1/2 text-emerald-300/50"></i>
                                <input
                                    type="tel"
                                    required
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-emerald-100/30 focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 focus:bg-white/10 outline-none transition-all text-sm font-medium"
                                    placeholder="Enter phone number"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-[10px] font-black text-emerald-300 uppercase tracking-widest mb-1.5">Date of Birth (For Birthday Gift!)</label>
                            <div className="relative">
                                <i className="fas fa-birthday-cake absolute left-4 top-1/2 -translate-y-1/2 text-emerald-300/50"></i>
                                <input
                                    type="date"
                                    required
                                    value={dob}
                                    onChange={(e) => setDob(e.target.value)}
                                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-emerald-100/30 focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 focus:bg-white/10 outline-none transition-all text-sm font-medium"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-gradient-to-r from-emerald-400 to-teal-500 hover:from-emerald-500 hover:to-teal-600 text-slate-950 font-black py-3.5 rounded-xl shadow-lg transition-all active:scale-98 disabled:opacity-50 text-sm tracking-wide mt-2"
                        >
                            {isLoading ? 'CREATING PROFILE...' : 'SIGN UP & BROWSE MENU'}
                        </button>
                    </form>
                )}

                {/* Secure info footer */}
                <div className="text-center mt-6 text-[10px] text-emerald-300/30 flex justify-center items-center gap-1">
                    <i className="fas fa-shield-alt"></i>
                    <span>Secure Loyalty Registration</span>
                </div>

            </div>
        </div>
    );
};

export default CustomerLoginSignup;
